/**
 * CreatePoolForm - Phase 3H-A (Dark Theme Fixed)
 *
 * Form for creating new pools with proper dark theme styling.
 * All text visible on dark backgrounds.
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Alert } from '../common';
import { AdminCreatePoolRequest, PoolDomain, PoolMode } from '../../types';

interface CreatePoolFormProps {
  onSubmit: (data: AdminCreatePoolRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

export function CreatePoolForm({
  onSubmit,
  onCancel,
  loading,
  error,
}: CreatePoolFormProps): JSX.Element {
  const [formData, setFormData] = useState({
    pool_name: '',
    pool_id: '',
    domain: 'nfl' as PoolDomain,
    mode: 'gm' as PoolMode,
    initial_bankroll: 1000,
    max_participants: 100,
    start_date: '',
    end_date: '',
  });

  const [autoGenerateId, setAutoGenerateId] = useState(true);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Auto-generate pool_id from name
    if (field === 'pool_name' && autoGenerateId) {
      const generatedId = String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      setFormData((prev) => ({ ...prev, pool_id: generatedId }));
    }
  };

  const handleDomainChange = (value: string) => {
    setFormData((prev) => ({ ...prev, domain: value as PoolDomain }));
  };

  const handleModeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, mode: value as PoolMode }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Build request - only include dates if they have values
    const request: AdminCreatePoolRequest = {
      pool_name: formData.pool_name,
      pool_id: autoGenerateId ? undefined : formData.pool_id,
      domain: formData.domain,
      mode: formData.mode,
      initial_bankroll: formData.initial_bankroll,
      max_participants: formData.max_participants,
      start_date: formData.start_date || undefined as unknown as string,
      end_date: formData.end_date || undefined as unknown as string,
    };
    
    await onSubmit(request);
  };

  const inputClasses = `
    w-full px-3 py-2 
    bg-slate-800 border border-slate-600 rounded-lg
    text-white placeholder-slate-500
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
  `;

  const selectClasses = `
    w-full px-3 py-2 
    bg-slate-800 border border-slate-600 rounded-lg
    text-white
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
  `;

  const labelClasses = "block text-sm font-medium text-slate-300 mb-1";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Pool</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <Alert variant="error">{error}</Alert>}

          {/* Pool Name */}
          <div>
            <label className={labelClasses}>
              Pool Name *
            </label>
            <input
              type="text"
              value={formData.pool_name}
              onChange={(e) => handleChange('pool_name', e.target.value)}
              className={inputClasses}
              placeholder="e.g., NFL Week 18 Challenge"
              required
            />
          </div>

          {/* Pool ID */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClasses.replace(' mb-1', '')}>
                Pool ID
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input
                  type="checkbox"
                  checked={autoGenerateId}
                  onChange={(e) => setAutoGenerateId(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                />
                Auto-generate
              </label>
            </div>
            <input
              type="text"
              value={formData.pool_id}
              onChange={(e) => {
                setAutoGenerateId(false);
                handleChange('pool_id', e.target.value);
              }}
              className={inputClasses}
              placeholder="e.g., nfl_week_18_challenge"
              disabled={autoGenerateId}
            />
            <p className="text-xs text-slate-500 mt-1">
              Lowercase with underscores, no spaces. Used in URLs and API calls.
            </p>
          </div>

          {/* Domain and Mode */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                Domain *
              </label>
              <select
                value={formData.domain}
                onChange={(e) => handleDomainChange(e.target.value)}
                className={selectClasses}
              >
                <option value="nfl" className="bg-slate-800">NFL</option>
                <option value="college" className="bg-slate-800">College Football</option>
                <option value="nba" className="bg-slate-800">NBA</option>
              </select>
            </div>
            <div>
              <label className={labelClasses}>
                Mode *
              </label>
              <select
                value={formData.mode}
                onChange={(e) => handleModeChange(e.target.value)}
                className={selectClasses}
              >
                <option value="gm" className="bg-slate-800">GM Mode (Portfolio)</option>
                <option value="pick" className="bg-slate-800">Pick Mode (Single)</option>
              </select>
            </div>
          </div>

          {/* Bankroll and Max Participants */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                Initial Bankroll
              </label>
              <input
                type="number"
                value={formData.initial_bankroll}
                onChange={(e) => handleChange('initial_bankroll', parseInt(e.target.value) || 0)}
                className={inputClasses}
                min={100}
                step={100}
              />
              <p className="text-xs text-slate-500 mt-1">
                Starting balance for each member
              </p>
            </div>
            <div>
              <label className={labelClasses}>
                Max Participants
              </label>
              <input
                type="number"
                value={formData.max_participants}
                onChange={(e) => handleChange('max_participants', parseInt(e.target.value) || 0)}
                className={inputClasses}
                min={2}
                max={10000}
              />
              <p className="text-xs text-slate-500 mt-1">
                Maximum members allowed
              </p>
            </div>
          </div>

          {/* Mode Description */}
          <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
            <h4 className="text-sm font-medium text-white mb-2">
              {formData.mode === 'gm' ? 'GM Mode' : 'Pick Mode'} Selected
            </h4>
            <p className="text-sm text-slate-300">
              {formData.mode === 'gm' ? (
                'Portfolio-based competition. Users build rosters of 7 player props with allocation percentages.'
              ) : (
                'Single-pick competition. Users select one outcome per market and stake a portion of their bankroll.'
              )}
            </p>
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                Start Date (optional)
              </label>
              <input
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className={inputClasses}
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <div>
              <label className={labelClasses}>
                End Date (optional)
              </label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className={inputClasses}
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !formData.pool_name.trim()}
            >
              {loading ? 'Creating...' : 'Create Pool'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default CreatePoolForm;
